import type { CityConfig } from "../hawaii-city/types";

export const lahainaConfig: CityConfig = {
  city: "Lahaina",
  state: "Hawaii",
  stateAbbr: "HI",
  slug: "lahaina",
  route: "/cities/lahaina",
  title: "Emergency & Expedited Passport Services in Lahaina, HI — West Maui | Fast Passport Center",
  metaDescription: "Emergency and expedited passport services in Lahaina, HI. Fast passport renewal and urgent passport help for West Maui, Kaanapali, and Napili travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Lahaina, HI",
  heroSubtitle: "Lahaina, Kaanapali, and West Maui residents and visitors — get your passport fast without leaving the island. Start online, ship overnight, and we handle the rest. As fast as 24 hours.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Historic%20Lahaina%20Maui%20Hawaii%20waterfront%20at%20golden%20hour%2C%20Front%20Street%20with%20palm%20trees%20and%20oceanfront%2C%20calm%20Pacific%20Ocean%20with%20sailboats%2C%20warm%20tropical%20sunset%20lighting%2C%20distant%20West%20Maui%20mountains%2C%20professional%20travel%20photography%2C%20vibrant%20warm%20colors%20with%20deep%20blue%20ocean%2C%20no%20readable%20text%20or%20signs&width=1800&height=700&seq=lahaina-hero-01&orientation=landscape",
  heroImageAlt: "Historic Lahaina Maui waterfront at sunset with ocean and palm trees",
  geoPosition: "20.8783;-156.6825",
  geoPlacename: "Lahaina, Hawaii",
  regionalAgency: {
    name: "Honolulu Passport Agency",
    address: "300 Ala Moana Blvd, Room 1-224, Honolulu, HI 96850",
    distance: "Inter-island flight to Honolulu — about 100 miles",
  },
  nearbyCitiesZips: [
    { name: "Lahaina", zips: "96761" },
    { name: "Kaanapali", zips: "96761" },
    { name: "Napili", zips: "96761" },
    { name: "Kahului", zips: "96732" },
    { name: "Wailuku", zips: "96793" },
    { name: "Kihei", zips: "96753" },
    { name: "Wailea", zips: "96753" },
    { name: "Paia", zips: "96779" },
    { name: "Makawao", zips: "96768" },
    { name: "Kula", zips: "96790" },
    { name: "Honolulu", zips: "96815" },
    { name: "Kailua", zips: "96734" },
    { name: "Hilo", zips: "96720" },
    { name: "Kailua-Kona", zips: "96740" },
    { name: "Honokowai", zips: "96761" },
    { name: "Kahului Airport", zips: "96732" },
  ],
  benefits: [
    { icon: "ri-computer-line", title: "Start 100% Online", desc: "No office visit required to begin. Complete your order from your Lahaina home or resort in minutes." },
    { icon: "ri-truck-line", title: "Ship Your Documents", desc: "Use overnight shipping from Lahaina or Kaanapali to send your documents directly to us." },
    { icon: "ri-shield-check-line", title: "Expert Document Review", desc: "Every application is reviewed before submission to reduce errors and delays." },
    { icon: "ri-calendar-check-line", title: "Reserved Agency Access", desc: "We hold reserved submission slots — no public appointment needed for West Maui residents." },
  ],
  testimonials: [
    {
      name: "Daniel R.",
      location: "Lahaina, HI",
      rating: 5,
      text: "I had a business trip to London in 5 days and my passport was expired. Fast Passport Center processed my renewal from Lahaina in 4 days. The shipping from West Maui was seamless and their team kept me updated.",
      service: "Passport Renewal",
    },
    {
      name: "Lisa M.",
      location: "Kaanapali, HI",
      rating: 5,
      text: "We were vacationing in Kaanapali and realized my husband's passport was missing 2 weeks before our European trip. FPC handled everything remotely and had a new passport shipped to our resort with time to spare.",
      service: "Lost Passport Replacement",
    },
    {
      name: "Mark T.",
      location: "Napili, HI",
      rating: 5,
      text: "Emergency trip to Canada for a family matter. I'm in Napili near Lahaina and FPC handled everything remotely. The passport arrived in 5 days. No need to fly to Honolulu for an agency appointment.",
      service: "Expedited Passport",
    },
  ],
  facilities: [
    {
      id: "lahaina-usps",
      name: "Lahaina Post Office",
      type: "USPS Passport Acceptance",
      address: "1760 Honoapiilani Hwy",
      city: "Lahaina",
      state: "HI",
      zip: "96761",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Downtown Lahaina",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Appointment required. Call ahead to confirm passport acceptance hours.",
      icon: "ri-mail-line",
    },
    {
      id: "kahului-usps-lahaina",
      name: "Kahului Post Office",
      type: "USPS Passport Acceptance",
      address: "138 Puunene Ave",
      city: "Kahului",
      state: "HI",
      zip: "96732",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "About 25 miles east",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Central Maui option. Confirm appointment and photo availability.",
      icon: "ri-mail-line",
    },
    {
      id: "wailuku-usps-lahaina",
      name: "Wailuku Post Office",
      type: "USPS Passport Acceptance",
      address: "250 Imi Kala St",
      city: "Wailuku",
      state: "HI",
      zip: "96793",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "About 22 miles east",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Wailuku area option. Confirm appointment and photo availability.",
      icon: "ri-mail-line",
    },
    {
      id: "kihei-usps-lahaina",
      name: "Kihei Post Office",
      type: "USPS Passport Acceptance",
      address: "1250 S Kihei Rd",
      city: "Kihei",
      state: "HI",
      zip: "96753",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "South Maui — about 30 miles south",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "South Maui option. Confirm appointment and photo availability.",
      icon: "ri-mail-line",
    },
  ],
  faqs: [
    {
      q: "How fast can I get a passport in Lahaina, HI?",
      a: "Through Fast Passport Center, expedited processing can be as fast as 24 hours. Standard processing through a Lahaina or Kahului post office can take 2–6 months. For West Maui residents, our courier service is the fastest option without flying to Honolulu.",
    },
    {
      q: "Do I need to fly to Honolulu for a passport?",
      a: "No. Most Lahaina and West Maui travelers start online and ship documents via overnight courier. The Lahaina, Kahului, or Wailuku Post Offices can serve as acceptance facilities if needed, but you don't need to visit the Honolulu agency.",
    },
    {
      q: "Where is the closest passport acceptance facility to Lahaina?",
      a: "The Lahaina Post Office at 1760 Honoapiilani Hwy accepts passport applications by appointment. Kahului Post Office and Wailuku Post Office are also options for Central Maui residents.",
    },
    {
      q: "What if I'm a tourist in Kaanapali or Lahaina and need a passport emergency?",
      a: "We frequently help tourists and vacationers in Lahaina, Kaanapali, and Napili who discover passport issues while traveling. We can ship to your resort or hotel address and coordinate return shipping so you don't miss your flight.",
    },
    {
      q: "Can I get an expedited passport if I've never had one before?",
      a: "Yes, but first-time applicants require an in-person acceptance clerk visit. The Lahaina or Kahului Post Office are the most convenient options for West Maui residents. We guide you through exactly what to bring.",
    },
    {
      q: "How does shipping from Lahaina work?",
      a: "You can ship overnight using FedEx or UPS from Lahaina or Kaanapali. Most West Maui residents use the FedEx location near Lahaina Cannery Mall or the UPS Store in Wailuku.",
    },
    {
      q: "Is Fast Passport Center an official government agency?",
      a: "No — Fast Passport Center is a private, approved and registered U.S. Department of State passport courier service. We work within the official passport system to provide expedited processing.",
    },
    {
      q: "What if I need a child passport in Lahaina?",
      a: "Child passports (for minors 15 and under) require parental participation and an in-person acceptance step — both parents typically must consent. We provide clear instructions for Lahaina and Kaanapali families.",
    },
    {
      q: "Can I get a passport if mine is lost or stolen?",
      a: "Yes. Lost or stolen passport replacements often require additional forms (including Form DS-64). We guide West Maui residents through the exact process, including the acceptance clerk visit and expedited submission.",
    },
  ],
  photoLocations: [
    { name: "Walgreens — Lahaina", address: "840 Waine'e St, Lahaina, HI 96761", hours: "Daily 7 AM – 11 PM" },
    { name: "Walgreens — Kahului", address: "45 Kamehameha Ave, Kahului, HI 96732", hours: "Daily 7 AM – 11 PM" },
    { name: "CVS Pharmacy — Lahaina", address: "130 Papalaua St, Lahaina, HI 96761", hours: "Daily 7 AM – 10 PM" },
    { name: "The UPS Store — Lahaina", address: "180 Dickenson St, Lahaina, HI 96761", hours: "Call for hours" },
    { name: "Walmart — Kahului", address: "101 Pakaula St, Kahului, HI 96732", hours: "Call for hours" },
  ],
  dmvLocations: [
    {
      name: "Lahaina DMV — Lahaina Satellite City Hall",
      address: "1034 Front St, Lahaina, HI 96761",
      hours: "Mon–Fri, 7:45 AM – 4:00 PM",
      note: "Driver licenses, REAL ID, state IDs. Appointment recommended.",
      featured: true,
    },
    {
      name: "Kahului DMV — Maui County Service Center",
      address: "70 E Kaahumanu Ave, Kahului, HI 96732",
      hours: "Mon–Fri, 7:45 AM – 4:00 PM",
      note: "Central Maui option. Appointment recommended.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "OGG",
      name: "Kahului Airport",
      distance: "About 25 miles east — Maui",
      address: "1 Keolani Pl, Kahului, HI 96732",
      note: "Major airport for Maui with direct flights to mainland U.S., Canada, and inter-island connections. Most West Maui travelers fly from OGG for international trips.",
      flightUrl: "https://www.hawaii.gov/ogg/",
      flightLabel: "OGG Airport Website",
      featured: true,
    },
    {
      code: "HNL",
      name: "Daniel K. Inouye International Airport",
      distance: "Inter-island flight — Honolulu",
      address: "300 Rodgers Blvd, Honolulu, HI 96819",
      note: "Major international hub. Many Maui travelers connect through HNL for international destinations.",
      flightUrl: "https://www.hawaii.gov/hnl/",
      flightLabel: "HNL Airport Website",
      featured: false,
    },
  ],
  services: [
    {
      icon: "ri-time-line",
      title: "Emergency Passport",
      description: "Flying in 24–72 hours? Our emergency courier service is designed for urgent, last-minute travel from Lahaina and West Maui.",
      bullets: ["As fast as 24 hours", "Reserved agency submission", "Live case tracking"],
      badge: "Same-Day Available",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Passport Renewal",
      description: "Already have a U.S. passport? We help West Maui travelers renew fast and avoid delays — no agency visit required for most renewals.",
      bullets: ["Eligible for expedited processing", "Ship documents overnight", "Professional document review"],
      badge: "Most Common",
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-file-add-line",
      title: "New Passport",
      description: "First-time applicants typically require an in-person acceptance clerk visit. We guide West Maui residents through every step.",
      bullets: ["Step-by-step checklist provided", "Acceptance clerk guidance", "Expedited after clerk visit"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-search-eye-line",
      title: "Lost or Stolen Passport",
      description: "Replacements often require additional forms. We guide you through exactly what's needed to avoid costly mistakes.",
      bullets: ["DS-64 form guidance included", "Replacement processing", "Urgent timelines available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Minors (15 and under) require parental participation and an in-person acceptance step. We provide clear instructions for Lahaina and Kaanapali families.",
      bullets: ["Both parents must consent", "Clear checklist provided", "Expedited options available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change",
      description: "After marriage, divorce, or a legal name change — we assist with corrected passports for West Maui residents.",
      bullets: ["Marriage certificate guidance", "Legal name change support", "Document review included"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
    {
      icon: "ri-book-2-line",
      title: "Second Passport & Damaged",
      description: "Need a second passport for frequent travel, or replacing a damaged one? We handle both efficiently.",
      bullets: ["Frequent traveler solutions", "Damaged passport replacement", "Expedited processing"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=second-passport",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for Lahaina and West Maui travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for West Maui residents with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance for West Maui residents with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for West Maui residents with lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16 in Lahaina and Kaanapali, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service for West Maui residents after marriage, divorce, or legal name changes." },
    { name: "Second Passport & Damaged", description: "Second passport issuance for frequent West Maui travelers and damaged passport replacement." },
  ],
  mistakes: [
    { icon: "ri-time-line", title: "Waiting Too Long to Apply", description: "Government processing times change constantly. Many West Maui travelers assume 6–8 weeks is safe, only to find out it's now 3–4 months. Apply as soon as you book travel." },
    { icon: "ri-camera-off-line", title: "Wrong Passport Photo", description: "Photos from drugstores and photo booths often get rejected for lighting, background, or expression issues. We provide exact specs so you get it right the first time." },
    { icon: "ri-file-damage-line", title: "Incomplete Forms", description: "One missing signature or wrong date and your application gets sent back. Our checklist catches errors before submission, saving you weeks of delay." },
    { icon: "ri-pen-nib-line", title: "Wrong Proof of Citizenship", description: "Not all birth certificates are accepted. Some require a long-form version. We tell you exactly which documents you need based on your situation." },
    { icon: "ri-calendar-close-line", title: "Missing Travel Proof for Emergency", description: "Regional agencies require proof of urgent travel. Without it, you're turned away. We help you prepare the right documentation before you need it." },
    { icon: "ri-map-pin-time-line", title: "Not Checking Name Consistency", description: "Your passport name must match your driver's license and ticket exactly. Middle names, suffixes, and hyphenated names cause more rejections than you'd think." },
  ],
  steps: [
    { number: "01", icon: "ri-computer-line", title: "Start Your Order Online", description: "Choose the processing speed that fits your travel date and complete your secure order. Takes just a few minutes." },
    { number: "02", icon: "ri-file-list-3-line", title: "We Confirm & Send Your Checklist", description: "We confirm your details and provide a personalized checklist, instructions, and document review guidance." },
    { number: "03", icon: "ri-send-plane-line", title: "Ship Your Documents Overnight", description: "Send your documents using an overnight shipping option from Lahaina or Kaanapali." },
    { number: "04", icon: "ri-building-2-line", title: "We Submit Through Reserved Access", description: "We submit through our reserved passport agency availability — so you don't have to fly to Honolulu." },
    { number: "05", icon: "ri-passport-line", title: "Your Passport Is Completed", description: "Your passport is completed within the timeframe you selected. No guessing, no hoping an appointment opens up." },
  ],
  processingOptions: [
    { speed: "24", unit: "Hours*", label: "Regional Agency Processing", tag: "Emergency Only", tagColor: "bg-gray-100 text-gray-500", features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to wait all day or return next day"], highlight: false, emergency: true },
    { speed: "2–9", unit: "Business Days", label: "Fast Passport Center", tag: "Most Popular", tagColor: "bg-[#F6C344] text-[#1A365D]", features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"], highlight: true },
    { speed: "3–5", unit: "Weeks", label: "Fast Passport Center", tag: "Standard Expedited", tagColor: "bg-[#1A365D]/10 text-[#1A365D]", features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"], highlight: false, smartSaver: true },
    { speed: "1–2", unit: "Months", label: "Self-Service (Post Office)", tag: "Slowest", tagColor: "bg-gray-100 text-gray-400", features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"], highlight: false, muted: true },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Yes — limited availability, often weeks out", fpc: "No appointment needed to start", fpcGood: true },
    { feature: "In-Person Visit", agency: "Required — fly to Honolulu", fpc: "Most travelers ship documents overnight from Lahaina", fpcGood: true },
    { feature: "Processing Speed", agency: "Urgent travel only — strict eligibility", fpc: "As fast as 24 hours — multiple speed options", fpcGood: true },
    { feature: "Document Review", agency: "None — errors cause rejection", fpc: "Professional review to reduce delays", fpcGood: true },
    { feature: "Courier Access", agency: "Not available to the public", fpc: "Reserved daily submission availability", fpcGood: true },
    { feature: "Predictability", agency: "Appointments can be cancelled or unavailable", fpc: "Predictable processing based on option selected", fpcGood: true },
    { feature: "Who Can Use It", agency: "Strict eligibility — urgent travel only", fpc: "Most U.S. citizens with required documents", fpcGood: true },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Acceptance Locations", href: "#acceptance-facilities" },
    { label: "vs. Regional Agency", href: "#comparison" },
    { label: "FAQ", href: "#faq" },
    { label: "Local Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Kahului", route: "/cities/kahului" },
    { name: "Kihei", route: "/cities/kihei" },
    { name: "Honolulu", route: "/cities/honolulu" },
    { name: "Kailua", route: "/cities/kailua" },
    { name: "Hilo", route: "/cities/hilo" },
    { name: "Kailua-Kona", route: "/cities/kailua-kona" },
  ],
  fpcOfficeAddress: "300 Ala Moana Blvd, Room 1-224, Honolulu, HI 96850",
  fpcOfficeNote: "After signing up online, choose what works best for you: courier pickup from your Lahaina or Kaanapali home, ship with our prepaid label from any UPS/FedEx location in Lahaina or Wailuku, or schedule a drop-off. Most West Maui clients ship overnight.",
  fpcOfficeDistance: "Serving Lahaina and all of West Maui remotely",
  serviceAreaIntro: "Whether you are in Lahaina, Kaanapali, Napili, or anywhere on West Maui, Fast Passport Center makes expedited passport processing simple — no flying to Honolulu, no agency lines.",
  nearbyCitiesIntro: "Anywhere on West Maui — start online, ship overnight from Lahaina or Kaanapali, and we handle the rest.",
  comparisonIntro: "",
  howItWorksIntro: "From start to finish, we make getting an expedited passport in Lahaina as simple as possible.",
  mistakesIntro: "These mistakes cause the most delays and rejections for West Maui travelers. Fast Passport Center reviews your documents before submission to catch these issues.",
  testimonialsIntro: "Real reviews from real travelers across Lahaina, Kaanapali, and Napili who trusted Fast Passport Center with their expedited passports.",
  faqIntro: "Everything West Maui travelers ask us most often about expedited passport services.",
  resourcesIntro: "",
  localTravelThemes: [],
  landmarks: [],
  island: "Maui",
  islandNickname: "The Valley Isle",
  landmarkCards: [
    {
      name: "Historic Front Street",
      description: "Once the royal capital of Hawaii, Lahaina's Front Street was one of the Pacific's most historic whaling ports. Today it's West Maui's cultural hub — galleries, restaurants, and Banyan Court Tree line the waterfront.",
      imageUrl: "https://readdy.ai/api/search-image?query=Historic%20Lahaina%20Front%20Street%20Maui%20Hawaii%20waterfront%2C%20charming%20buildings%20along%20ocean%20boulevard%2C%20palm%20trees%2C%20boats%20in%20harbor%2C%20warm%20sunset%20light%2C%20professional%20travel%20photography%2C%20vibrant%20coastal%20atmosphere%2C%20no%20readable%20text%20or%20signs&width=600&height=400&seq=lahaina-front-st&orientation=landscape",
    },
    {
      name: "Kaanapali Beach",
      description: "Three miles of golden sand fronting West Maui's premier resort strip. Kaanapali Beach is one of Hawaii's finest — and travelers here from Japan, Canada, and Australia are a daily presence.",
      imageUrl: "https://readdy.ai/api/search-image?query=Kaanapali%20Beach%20West%20Maui%20Hawaii%2C%20wide%20golden%20sand%20beach%20with%20luxury%20resort%20hotels%2C%20turquoise%20calm%20ocean%2C%20tropical%20palm%20trees%2C%20bright%20sunny%20day%2C%20professional%20travel%20photography%2C%20stunning%20beach%20destination%2C%20no%20readable%20text%20or%20signs&width=600&height=400&seq=lahaina-kaanapali&orientation=landscape",
    },
    {
      name: "Lahaina Harbor",
      description: "Lahaina Harbor is West Maui's maritime center — whale watching cruises, snorkel charters, and inter-island ferry service all depart from here. Ocean adventures here often inspire new international travel plans.",
      imageUrl: "https://readdy.ai/api/search-image?query=Lahaina%20Harbor%20Maui%20Hawaii%2C%20small%20boat%20harbor%20with%20fishing%20boats%20and%20charter%20vessels%2C%20palm%20trees%20and%20town%20in%20background%2C%20turquoise%20water%2C%20warm%20afternoon%20light%2C%20professional%20travel%20photography%2C%20no%20readable%20text%20or%20signs&width=600&height=400&seq=lahaina-harbor&orientation=landscape",
    },
  ],
  travelTips: [
    { icon: "ri-ship-line", title: "Whale Watching Season", description: "Humpback whales migrate to Maui's waters December through April. Many Lahaina visitors come for whale season and book Asia or Pacific trips next — keep passports current year-round." },
    { icon: "ri-flight-takeoff-line", title: "OGG Is 25 Miles East", description: "Lahaina residents reach Kahului Airport (OGG) via Honoapiilani Highway. Budget 30–45 minutes and check passport expiration at least 6 months before any international flight." },
    { icon: "ri-hotel-line", title: "Resort Guest Services", description: "Guests at Kaanapali resorts, Hyatt Regency, or Sheraton Maui who discover passport problems — we can process urgently and ship returns to your Kaanapali resort address." },
    { icon: "ri-truck-line", title: "Ship from West Maui", description: "The UPS Store near Lahaina Cannery Mall and FedEx in Wailuku serve West Maui residents. Overnight shipping cutoffs are around 5:30–6pm for next-morning delivery." },
    { icon: "ri-global-line", title: "International Tourism Hub", description: "West Maui’s resort corridor draws visitors from Japan, Canada, Australia, and Korea year-round. International passport requirements are well-understood here — stay current." },
    { icon: "ri-sun-line", title: "Napili & Honokawai Community", description: "Long-term residents of Napili, Honokowai, and Kahana make Lahaina their town center. We process passports for all West Maui communities — from the harbor to the ridge." },
  ],
  statePageRoute: "/states/hawaii",
  statePageName: "Hawaii",
  dmvWebsite: "https://www.hawaii.gov/dot/highways/mvm",
  dmvLabel: "Hawaii DMV — Official Website",
};